import React from 'react';
import { X, Calculator, Check, TrendingUp, Building, Layers } from 'lucide-react';
import { HighlightArea } from '../types';

interface HighlightCalculationsPopupProps {
  highlightArea: HighlightArea;
  calculations: {
    area: number;
    hlp: number;
    pw: number;
    sw: number;
  };
  onClose: () => void;
  onSubmit?: (floorLevel: string, buildingType: string) => void;
  position?: { x: number; y: number };
}

const HighlightCalculationsPopup: React.FC<HighlightCalculationsPopupProps> = ({
  highlightArea,
  calculations,
  onClose,
  onSubmit,
  position = { x: 50, y: 50 }
}) => {
  const [floorLevel, setFloorLevel] = React.useState('ground floor');
  const [buildingType, setBuildingType] = React.useState('ext 1');

  const buildingTypeOptions = [
    { value: 'ext 1', label: 'Ext 1' },
    { value: 'ext 2', label: 'Ext 2' },
    { value: 'ext 3', label: 'Ext 3' },
    { value: 'ext 4', label: 'Ext 4' },
    { value: 'ext 5', label: 'Ext 5' },
  ];

  const floorLevelOptions = [
    { value: 'basement', label: 'Basement' },
    { value: 'ground floor', label: 'Ground Floor' },
    { value: 'first floor', label: 'First Floor' },
    { value: 'second floor', label: 'Second Floor' },
    { value: 'third floor', label: 'Third Floor' },
    { value: 'fourth floor', label: 'Fourth Floor' },
    { value: 'fifth floor', label: 'Fifth Floor' },
  ];

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(floorLevel, buildingType);
    }
    onClose();
  };

  // Handle ESC key to close modal
  React.useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const totalWallLength = calculations.hlp + calculations.pw + calculations.sw;

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Centered modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm sm:max-w-md lg:max-w-lg xl:max-w-xl mx-4 transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95">
          
          {/* Header */}
          <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Calculator size={18} className="sm:w-5 sm:h-5" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold">Area Calculations</h3>
                  <p className="text-blue-100 text-xs sm:text-sm">Wall type analysis</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
              >
                <X size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* Configuration Section */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <Building size={14} className="sm:w-4 sm:h-4" />
                Configuration
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Floor Level
                  </label>
                  <div className="relative">
                    <Layers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    <select
                      value={floorLevel}
                      onChange={(e) => setFloorLevel(e.target.value)}
                      className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm bg-white transition-all duration-200"
                    >
                      {floorLevelOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Building Type
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                    <select
                      value={buildingType}
                      onChange={(e) => setBuildingType(e.target.value)}
                      className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm bg-white transition-all duration-200"
                    >
                      {buildingTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Calculations Section */}
            <div className="space-y-3 sm:space-y-4">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                <TrendingUp size={14} className="sm:w-4 sm:h-4" />
                Measurements
              </h4>
              
              <div className="space-y-2 sm:space-y-3">
                {/* Area */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 sm:p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-gray-600 rounded-full"></div>
                      <span className="text-xs sm:text-sm font-semibold text-gray-700">Total Area</span>
                    </div>
                    <span className="text-base sm:text-lg font-bold text-gray-900">
                      {calculations.area.toFixed(2)} m²
                    </span>
                  </div>
                </div>

                {/* Wall Types */}
                <div className="space-y-2">
                  {/* HLP */}
                  <div className="bg-gradient-to-r from-red-50 to-red-100 p-3 sm:p-4 rounded-xl border border-red-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                        <span className="text-xs sm:text-sm font-semibold text-red-700">
                          <span className="sm:hidden">HLP</span>
                          <span className="hidden sm:inline">HLP (Heat Loss Parameter)</span>
                        </span>
                      </div>
                      <span className="text-base sm:text-lg font-bold text-red-700">
                        {calculations.hlp.toFixed(2)} m
                      </span>
                    </div>
                  </div>

                  {/* PW */}
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-500 rounded-full"></div>
                        <span className="text-xs sm:text-sm font-semibold text-blue-700">
                          <span className="sm:hidden">PW</span>
                          <span className="hidden sm:inline">PW (Party Wall)</span>
                        </span>
                      </div>
                      <span className="text-base sm:text-lg font-bold text-blue-700">
                        {calculations.pw.toFixed(2)} m
                      </span>
                    </div>
                  </div>

                  {/* SW */}
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-3 sm:p-4 rounded-xl border border-orange-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-orange-500 rounded-full"></div>
                        <span className="text-xs sm:text-sm font-semibold text-orange-700">
                          <span className="sm:hidden">SW</span>
                          <span className="hidden sm:inline">SW (Sheltered Wall)</span>
                        </span>
                      </div>
                      <span className="text-base sm:text-lg font-bold text-orange-700">
                        {calculations.sw.toFixed(2)} m
                      </span>
                    </div>
                  </div>
                </div>

                {/* Total Wall Length */}
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 p-3 sm:p-4 rounded-xl border border-indigo-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 bg-indigo-600 rounded-full"></div>
                      <span className="text-xs sm:text-sm font-semibold text-indigo-700">Total Wall Length</span>
                    </div>
                    <span className="text-base sm:text-lg font-bold text-indigo-700">
                      {totalWallLength.toFixed(2)} m
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 sm:py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors duration-200 font-medium text-sm sm:text-base"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2.5 sm:py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium shadow-lg text-sm sm:text-base"
              >
                <Check size={14} className="sm:w-4 sm:h-4" />
                Save to Notes
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-50 rounded-b-2xl border-t border-gray-100">
            <div className="text-xs text-gray-500 text-center">
              Press <kbd className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-200 rounded text-gray-700 text-xs">ESC</kbd> to close
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HighlightCalculationsPopup; 